import React, { useCallback, useEffect, useRef, useState } from "react";

import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@material-ui/core";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";

// Guarda de fechamento de chamado. O backend é quem decide e responde:
//   409 ERR_TICKET_CLOSE_WITHOUT_INTERACTION          -> config `ticketCloseWithoutInteraction`
//       em "warn": o atendente nunca falou com o contato, pedimos confirmação
//   403 ERR_TICKET_CLOSE_WITHOUT_INTERACTION_BLOCKED  -> modo "blocked": toast e não fecha
//   409 ERR_TICKET_CLOSE_ASK_COMPLETION_MESSAGE       -> config `askCompletionMessageOnClose`
//       ligada e a conexão tem mensagem de conclusão: perguntamos se ela deve ser enviada
// Cada pergunta respondida volta no retry (`confirmedWithoutInteraction`,
// `sendCompletionMessage`). Assim todos os pontos de fechamento do frontend
// compartilham os mesmos diálogos.
//
// Ação em massa: passe o mesmo objeto `batch` em todas as chamadas do lote para
// cada pergunta ser feita uma vez só (a resposta fica nele e vale para o lote).
// Cancelar marca `batch.cancelled`, e o chamador interrompe o lote.
const MAX_CLOSE_ATTEMPTS = 3;

const useCloseTicketGuard = () => {
  // { isBatch } enquanto a confirmação de finalizar sem interação está aberta
  const [confirmDialog, setConfirmDialog] = useState(null);
  const pendingResolve = useRef(null);

  // { preview, isBatch } enquanto a pergunta da mensagem de conclusão está aberta
  const [completionDialog, setCompletionDialog] = useState(null);
  const completionResolve = useRef(null);

  // Quem usa o guarda saiu da tela (ex.: a lista desmontada no meio de uma ação
  // em massa): a pergunta aberta, ou uma que viria depois, conta como cancelada;
  // senão a Promise nunca resolve e o chamador fica parado
  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      const resolveConfirm = pendingResolve.current;
      const resolveCompletion = completionResolve.current;
      pendingResolve.current = null;
      completionResolve.current = null;
      if (resolveConfirm) resolveConfirm(false);
      if (resolveCompletion) resolveCompletion(null);
    };
  }, []);

  const askConfirmation = useCallback((isBatch) => {
    if (!isMountedRef.current) return Promise.resolve(false);
    return new Promise((resolve) => {
      pendingResolve.current = resolve;
      setConfirmDialog({ isBatch });
    });
  }, []);

  const finishConfirmation = useCallback((confirmed) => {
    setConfirmDialog(null);
    const resolve = pendingResolve.current;
    pendingResolve.current = null;
    if (resolve) resolve(confirmed);
  }, []);

  // true = enviar | false = não enviar | null = cancelou a finalização
  const askCompletionMessage = useCallback((preview, isBatch) => {
    if (!isMountedRef.current) return Promise.resolve(null);
    return new Promise((resolve) => {
      completionResolve.current = resolve;
      setCompletionDialog({ preview: preview || "", isBatch });
    });
  }, []);

  const finishCompletionMessage = useCallback((answer) => {
    setCompletionDialog(null);
    const resolve = completionResolve.current;
    completionResolve.current = null;
    if (resolve) resolve(answer);
  }, []);

  // Retorna true quando o chamado foi finalizado; false quando cancelado/bloqueado/erro.
  // `showError: false` serve para ações em massa, que exibem o próprio resumo no fim.
  const closeTicket = useCallback(
    async (ticketId, payload = {}, { showError = true, batch } = {}) => {
      let body = { ...payload, status: "closed" };
      if (typeof batch?.sendCompletionMessage === "boolean") {
        body.sendCompletionMessage = batch.sendCompletionMessage;
      }
      if (batch?.confirmedWithoutInteraction) {
        body.confirmedWithoutInteraction = true;
      }

      for (let attempt = 0; attempt < MAX_CLOSE_ATTEMPTS; attempt += 1) {
        try {
          await api.put(`/tickets/${ticketId}`, body);
          return true;
        } catch (err) {
          const data = err?.response?.data;
          const errorCode = data?.error;

          if (
            errorCode === "ERR_TICKET_CLOSE_WITHOUT_INTERACTION" &&
            !body.confirmedWithoutInteraction
          ) {
            const confirmed = await askConfirmation(Boolean(batch));
            if (!confirmed) {
              if (batch) batch.cancelled = true;
              return false;
            }
            if (batch) batch.confirmedWithoutInteraction = true;
            body = { ...body, confirmedWithoutInteraction: true };
            continue;
          }

          if (
            errorCode === "ERR_TICKET_CLOSE_ASK_COMPLETION_MESSAGE" &&
            body.sendCompletionMessage === undefined
          ) {
            const answer = await askCompletionMessage(
              data?.completionMessage,
              Boolean(batch)
            );
            if (answer === null) {
              if (batch) batch.cancelled = true;
              return false;
            }
            if (batch) batch.sendCompletionMessage = answer;
            body = { ...body, sendCompletionMessage: answer };
            continue;
          }

          if (showError) toastError(err);
          return false;
        }
      }

      return false;
    },
    [askConfirmation, askCompletionMessage]
  );

  const dialog = (
    <>
      <Dialog
        open={Boolean(confirmDialog)}
        onClose={() => finishConfirmation(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {i18n.t("ticketActionButtons.withoutInteractionTitle")}
        </DialogTitle>
        <DialogContent dividers>
          <Typography>
            {i18n.t("ticketActionButtons.withoutInteractionMessage")}
          </Typography>
          {confirmDialog?.isBatch && (
            <Typography
              variant="body2"
              color="textSecondary"
              style={{ marginTop: 12 }}
            >
              {i18n.t("ticketActionButtons.completionMessageBatchHint")}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => finishConfirmation(false)} color="default">
            {i18n.t("confirmationModal.buttons.cancel")}
          </Button>
          <Button
            onClick={() => finishConfirmation(true)}
            color="secondary"
            variant="contained"
          >
            {i18n.t("confirmationModal.buttons.confirm")}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(completionDialog)}
        onClose={() => finishCompletionMessage(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {i18n.t("ticketActionButtons.completionMessageTitle")}
        </DialogTitle>
        <DialogContent dividers>
          <Typography>
            {i18n.t("ticketActionButtons.completionMessageText")}
          </Typography>
          <Typography
            variant="body2"
            component="div"
            style={{
              marginTop: 12,
              padding: 12,
              borderRadius: 4,
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
              maxHeight: 240,
              overflowY: "auto",
              background: "rgba(127, 127, 127, 0.12)",
            }}
          >
            {completionDialog?.preview}
          </Typography>
          {completionDialog?.isBatch && (
            <Typography
              variant="body2"
              color="textSecondary"
              style={{ marginTop: 12 }}
            >
              {i18n.t("ticketActionButtons.completionMessageBatchHint")}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => finishCompletionMessage(null)} color="default">
            {i18n.t("confirmationModal.buttons.cancel")}
          </Button>
          <Button onClick={() => finishCompletionMessage(false)} color="secondary">
            {i18n.t("ticketActionButtons.completionMessageSkip")}
          </Button>
          <Button
            onClick={() => finishCompletionMessage(true)}
            color="primary"
            variant="contained"
          >
            {i18n.t("ticketActionButtons.completionMessageSend")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );

  return { closeTicket, dialog };
};

export default useCloseTicketGuard;
